/**
 * PrintHQ Relay WebSocket Client
 *
 * Connects the Dashboard to the PrintHQ WebSocket relay server (wss://ws.printhq.io)
 * as role=dashboard. Receives real-time printer status updates directly from the Bridge,
 * bypassing the 10-second Supabase polling round-trip.
 *
 * Supports both JSON text messages (printer status, commands) and binary messages
 * (camera frames). Binary frame format: [1 byte: cameraId length][cameraId UTF-8][JPEG data]
 */

const RELAY_URL = import.meta.env.VITE_RELAY_URL || 'wss://ws.printhq.io'
const RECONNECT_DELAY_MS = 3_000
const MAX_RECONNECT_DELAY_MS = 30_000

type MessageHandler = (msg: any) => void
type BinaryFrameHandler = (cameraId: string, jpegData: Blob) => void

class RelayService {
  private ws: WebSocket | null = null
  private reconnectDelay = RECONNECT_DELAY_MS
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private destroyed = false
  private token: string | null = null
  private handlers = new Set<MessageHandler>()
  private binaryHandlers = new Set<BinaryFrameHandler>()

  /** Connect to the relay server with a Supabase JWT */
  connect(token: string): void {
    this.token = token
    this.destroyed = false
    this.doConnect()
  }

  private doConnect(): void {
    if (this.destroyed || !this.token) return

    // Clean up existing connection
    if (this.ws) {
      this.ws.onopen = null
      this.ws.onmessage = null
      this.ws.onclose = null
      this.ws.onerror = null
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close()
      }
      this.ws = null
    }

    const url = `${RELAY_URL}?token=${this.token}&role=dashboard`
    console.log('[relay] Connecting to relay server...')

    this.ws = new WebSocket(url)
    this.ws.binaryType = 'arraybuffer'

    this.ws.onopen = () => {
      console.log('[relay] Connected to relay server')
      this.reconnectDelay = RECONNECT_DELAY_MS
    }

    this.ws.onmessage = (event) => {
      // Binary message — camera frame
      if (event.data instanceof ArrayBuffer) {
        this.handleBinaryFrame(event.data)
        return
      }

      // Text message — JSON
      try {
        const msg = JSON.parse(event.data as string)
        for (const handler of this.handlers) {
          handler(msg)
        }
      } catch {
        // ignore malformed messages
      }
    }

    this.ws.onclose = (event) => {
      console.log(`[relay] Disconnected (${event.code}: ${event.reason})`)
      this.ws = null
      if (!this.destroyed) this.scheduleReconnect()
    }

    this.ws.onerror = () => {
      // onclose will fire after this
    }
  }

  /** Parse binary frame: [1 byte: id length][cameraId][JPEG data] */
  private handleBinaryFrame(buffer: ArrayBuffer): void {
    if (this.binaryHandlers.size === 0) return

    const view = new DataView(buffer)
    if (buffer.byteLength < 2) return

    const idLen = view.getUint8(0)
    if (buffer.byteLength < 1 + idLen + 1) return

    const idBytes = new Uint8Array(buffer, 1, idLen)
    const cameraId = new TextDecoder().decode(idBytes)
    const jpegData = new Blob([new Uint8Array(buffer, 1 + idLen)], { type: 'image/jpeg' })

    for (const handler of this.binaryHandlers) {
      handler(cameraId, jpegData)
    }
  }

  /** Register a handler for incoming JSON messages */
  onMessage(handler: MessageHandler): () => void {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  /** Register a handler for incoming binary camera frames */
  onCameraFrame(handler: BinaryFrameHandler): () => void {
    this.binaryHandlers.add(handler)
    return () => this.binaryHandlers.delete(handler)
  }

  /** Send a JSON message to the bridge via relay */
  send(data: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data))
    }
  }

  /** Send a printer command to the bridge */
  sendCommand(printerId: string, command: string): void {
    this.send({
      type: 'printer.command',
      printerId,
      data: { command },
    })
  }

  /** Check if connected */
  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  /** Disconnect and stop reconnecting */
  disconnect(): void {
    this.destroyed = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      this.ws.onclose = null // prevent reconnect
      this.ws.close(1000, 'Dashboard disconnecting')
      this.ws = null
    }
    this.token = null
  }

  /** Reconnect with a new token (e.g. after token refresh) */
  reconnect(token: string): void {
    this.disconnect()
    this.connect(token)
  }

  private scheduleReconnect(): void {
    if (this.destroyed || this.reconnectTimer) return

    console.log(`[relay] Reconnecting in ${this.reconnectDelay / 1000}s...`)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.doConnect()
    }, this.reconnectDelay)

    this.reconnectDelay = Math.min(this.reconnectDelay * 2, MAX_RECONNECT_DELAY_MS)
  }
}

/** Singleton relay instance */
export const relay = new RelayService()
